import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import App from 'common/backbone-app';
import Wahanda from 'common/wahanda';
import { BookingsListAnalytics } from 'common/analytics';
import { parseBookingsFiltersFromWindowHash } from 'src/url-states/reports-bookings';
import { CUSTOM_DATE_FILTER_REGEX, getFilterDates, getRange } from 'services/bookings-report';

export type MenuState = 'type' | 'subtype' | 'date' | null;

type CalendarDrawerHost = HTMLDivElement & { _reactRootContainer?: unknown };

const HOME_TABLE_ENTITY_TYPES: Partial<Record<string, string>> = {
  BKN: 'bookings',
  REV: 'evouchers-redeemed',
  AEV: 'evouchers-active',
};

export const useBookingsMountGuard = (fetchTsRef: React.MutableRefObject<number>) => {
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      fetchTsRef.current = 0;
    };
  }, [fetchTsRef]);

  return isMountedRef;
};

export const useHydrateBookingsFilters = ({
  storageKey,
  setFilterType,
  setFilterDate,
  setHydratedFromHash,
}: {
  storageKey: string;
  setFilterType: React.Dispatch<React.SetStateAction<string>>;
  setFilterDate: React.Dispatch<React.SetStateAction<string>>;
  setHydratedFromHash: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  useLayoutEffect(() => {
    const hash = parseBookingsFiltersFromWindowHash();
    if (hash) {
      setFilterType(hash.type);
      setFilterDate(hash.date);
    } else {
      const stored = Wahanda.LocalStorage.get(storageKey);
      if (stored) {
        setFilterType(stored);
      }
    }
    setHydratedFromHash(true);
  }, [setFilterType, setFilterDate, setHydratedFromHash, storageKey]);
};

export const useBookingsPageView = (hydratedFromHash: boolean) => {
  useEffect(() => {
    if (!hydratedFromHash) {
      return;
    }
    BookingsListAnalytics.trackPageView();
  }, [hydratedFromHash]);
};

export const useBookingsFetchOnFilters = ({
  hydratedFromHash,
  filterType,
  filterDate,
  fetchRows,
}: {
  hydratedFromHash: boolean;
  filterType: string;
  filterDate: string;
  fetchRows: () => void;
}) => {
  useEffect(() => {
    if (!hydratedFromHash) {
      return;
    }
    fetchRows();
  }, [filterType, filterDate, hydratedFromHash, fetchRows]);
};

const emitBookingsHomeTableFiltered = (filterType: string, filterDate: string) => {
  const dates = getFilterDates({ type: filterType, date: filterDate });
  const entityTypes = HOME_TABLE_ENTITY_TYPES[filterType] ?? filterType;
  App.trigger(Wahanda.Event.HOME_TABLE_FILTERED, {
    dateFrom: dates.from,
    dateTo: dates.to,
    entityTypes,
  });
};

export const useBookingsHomeTableFilteredDispatcher = () =>
  useCallback((filterType: string, filterDate: string) => {
    emitBookingsHomeTableFiltered(filterType, filterDate);
  }, []);

const getBookingsModelRefreshEventNames = (): string => {
  const e = Wahanda.Event;
  return [
    e.APPOINTMENT_CONFIRMED,
    e.APPOINTMENT_REJECTED,
    e.APPOINTMENT_CANCELLED,
    e.APPOINTMENT_CHECKED_OUT,
    e.BOOKING_CONFIRMED,
    e.BOOKING_REJECTED,
    e.EVOUCHER_REDEEMED,
    e.APPOINTMENT_SET_NOSHOW,
    e.APPOINTMENT_GROUP_CONFIRMED,
    e.APPOINTMENT_GROUP_REJECTED,
    e.APPOINTMENT_GROUP_SET_NOSHOW,
    e.APPOINTMENT_UPDATED,
  ].join(' ');
};

export const useBookingsRefreshEvents = (fetchRows: () => void) => {
  useEffect(() => {
    const refreshEvents = getBookingsModelRefreshEventNames();
    const onRefresh = () => fetchRows();
    App.on(refreshEvents, onRefresh);
    return () => {
      App.off(refreshEvents, onRefresh);
    };
  }, [fetchRows]);
};

export const useBookingsDrawerCloseHandler = () => {
  useEffect(() => {
    const onDrawerClose = () => {
      const node = document.getElementById('calendar-drawer-container');
      if (node) {
        const reactRootContainer = (node as CalendarDrawerHost)._reactRootContainer;

        if (!reactRootContainer) {
          return;
        }

        try {
          ReactDOM.unmountComponentAtNode(node);
        } catch (_err) {
          console.error('Error unmounting component at node', _err);
        }
      }
    };
    App.on(Wahanda.Event.CALENDAR_DRAWER_CLOSE, onDrawerClose);
    return () => {
      App.off(Wahanda.Event.CALENDAR_DRAWER_CLOSE, onDrawerClose);
    };
  }, []);
};

export const useBookingsOutsideClick = (
  setOpenMenu: React.Dispatch<React.SetStateAction<MenuState>>,
) => {
  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (target instanceof Element && target.closest('.home-filters')) {
        return;
      }
      setOpenMenu(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [setOpenMenu]);
};

export const useBookingsDatepicker = ({
  showCustomRange,
  dateFromRef,
  dateToRef,
  filterType,
  filterDate,
  customFilterSetRef,
}: {
  showCustomRange: boolean;
  dateFromRef: React.RefObject<HTMLInputElement>;
  dateToRef: React.RefObject<HTMLInputElement>;
  filterType: string;
  filterDate: string;
  customFilterSetRef: React.MutableRefObject<boolean>;
}) => {
  useEffect(() => {
    if (!showCustomRange || !dateFromRef.current || !dateToRef.current) {
      return;
    }
    const $from = $(dateFromRef.current);
    const $to = $(dateToRef.current);
    $from.venueDatepicker({
      onSelect(selectedDate: Date) {
        $to.datepicker('option', 'minDate', selectedDate);
        $from.valid();
      },
    });
    $to.venueDatepicker({
      onSelect(selectedDate: Date) {
        $from.datepicker('option', 'maxDate', selectedDate);
        $to.valid();
      },
    });
    const range = getRange({ type: filterType, date: filterDate });
    const customRangeMatch = filterDate.match(CUSTOM_DATE_FILTER_REGEX);
    if (customRangeMatch) {
      $from.datepicker('setDate', Wahanda.Date.createDate(customRangeMatch[1]));
      $to.datepicker('setDate', Wahanda.Date.createDate(customRangeMatch[2]));
    } else if (!customFilterSetRef.current) {
      if (range.from) {
        $from.val(Wahanda.Date.formatToDefaultDate(range.from));
      }
      if (range.to) {
        $to.val(Wahanda.Date.formatToDefaultDate(range.to));
      }
    }
    return () => {
      if ($from.data('datepicker')) {
        $from.datepicker('destroy');
      }
      if ($to.data('datepicker')) {
        $to.datepicker('destroy');
      }
    };
  }, [showCustomRange, filterDate, filterType, dateFromRef, dateToRef, customFilterSetRef]);
};

export const useBookingsCustomRangeValidation = ({
  showCustomRange,
  customRangeFormRef,
}: {
  showCustomRange: boolean;
  customRangeFormRef: React.RefObject<HTMLFormElement>;
}) => {
  useEffect(() => {
    if (!showCustomRange) {
      return;
    }
    const validations = Wahanda.Validate.getValidations('defaults', {
      submitHandler() {},
    });
    const formEl = customRangeFormRef.current;
    if (formEl) {
      $(formEl).validate(validations);
    }
  }, [showCustomRange, customRangeFormRef]);
};
